import { useQuery } from '@tanstack/react-query';
import api from '../../services/api';
import { Card, CardHeader, CardTitle, CardContent } from '../common/Card';
import { Badge } from '../common/Badge';
import { ProgressBar } from '../common/ProgressBar';
import { mapApiLeaveBalanceToLeaveBalance } from '../../utils/apiMappers';
import type { LeaveBalance as LeaveBalanceType } from '../../types/frontendTypes';

// Use the proper type from types file
// interface LeaveBalanceData is replaced by LeaveBalanceType

export const LeaveBalance = () => {
  const currentYear = new Date().getFullYear();
  
  const { data: balances, isLoading, error } = useQuery({
    queryKey: ['leaveBalances', currentYear],
    queryFn: async () => {
      console.log('Fetching leave balances for year:', currentYear);
      const response = await api.get('/leaves/balances', {
        params: { year: currentYear }
      });
      console.log('Leave balances response:', response.data);
      return response.data || [];
    }
  });

  // Debug logging
  console.log('LeaveBalance component - balances:', balances, 'isLoading:', isLoading, 'error:', error);

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="p-6 text-center">
          <div className="text-red-600">
            <p className="font-medium">Błąd ładowania sal urlopowych</p>
            <p className="text-sm mt-1">{error.message}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Map backend data to frontend format using proper mapper
  const leaveBalances: LeaveBalanceType[] = (balances || []).map((balance: any) => 
    mapApiLeaveBalanceToLeaveBalance(balance)
  );

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium text-gray-900">Bilans urlopowy - {currentYear}</h3>
      
      {leaveBalances.length === 0 ? (
        <Card>
          <CardContent className="p-6 text-center text-gray-500">
            Brak danych o bilansie urlopowym na ten rok
          </CardContent>
        </Card>
      ) : (
        leaveBalances.map((balance) => {
          const totalBalance = balance.initialBalance;
          const hasLimit = totalBalance > 0;
          const usagePercentage = hasLimit 
            ? Math.min(100, Math.round((balance.used / totalBalance) * 100))
            : 0;
          const remainingPercentage = hasLimit 
            ? Math.min(100, Math.round((balance.remaining / totalBalance) * 100))
            : 100;
          
          return (
            <Card key={balance.id}>
              <CardContent className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h4 className="text-sm font-medium text-gray-900">
                      {balance.leaveType?.name || 'Unknown Leave Type'}
                    </h4>
                    <p className="text-sm text-gray-500 mt-1">
                      Przysługuje: {balance.initialBalance > 0 ? `${balance.initialBalance} dni` : 'bez limitu'}
                      {balance.carriedOver > 0 && (
                        <span className="ml-2">
                          (Przeniesione: {balance.carriedOver} dni)
                        </span>
                      )}
                    </p>
                  </div>
                  <Badge variant={balance.remaining > 0 ? 'success' : balance.initialBalance === 0 ? 'primary' : 'error'}>
                    {balance.remaining || 0} dni pozostało
                  </Badge>
                </div>

                <div className="space-y-3">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-600">Wykorzystane</span>
                      <span className="font-medium">{balance.used || 0} dni {hasLimit && `(${usagePercentage}%)`}</span>
                    </div>
                    {hasLimit && (
                      <ProgressBar 
                        value={usagePercentage} 
                        variant="primary"
                        size="sm"
                      />
                    )}
                  </div>
                  
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-600">Dostępne</span>
                      <span className="font-medium">{balance.remaining || 0} dni {hasLimit && `(${remainingPercentage}%)`}</span>
                    </div>
                    {hasLimit && (
                      <ProgressBar 
                        value={remainingPercentage} 
                        variant="success"
                        size="sm"
                      />
                    )}
                  </div>
                </div>

                {balance.adjustments !== 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <p className="text-sm text-gray-600">
                      Korekty: {balance.adjustments > 0 ? '+' : ''}{balance.adjustments || 0} dni
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })
      )}
    </div>
  );
};